import React from 'react';

import styled from 'styled-components';

import { CATEGORY_INSIGHT_VARIANT } from './constants';
import { InsightVariant } from './types';

const SECTION_ICON_SRC: Record<InsightVariant, string> = {
  [CATEGORY_INSIGHT_VARIANT.STRENGTHS]: '/icons/sentiment-satisfied-green.svg',
  [CATEGORY_INSIGHT_VARIANT.DIFFERENCES]: '/icons/signal-cellular-blue.svg',
  [CATEGORY_INSIGHT_VARIANT.WEAKNESSES]: '/icons/unsatisfied-orange.svg',
  [CATEGORY_INSIGHT_VARIANT.ACTIONS]: '/icons/checklist-red.svg',
};

const SECTION_ICON_BACKGROUND: Record<InsightVariant, string> = {
  [CATEGORY_INSIGHT_VARIANT.STRENGTHS]: 'var(--positive-green-light)',
  [CATEGORY_INSIGHT_VARIANT.DIFFERENCES]: 'var(--pale-blue)',
  [CATEGORY_INSIGHT_VARIANT.WEAKNESSES]: 'var(--pale-yellow)',
  [CATEGORY_INSIGHT_VARIANT.ACTIONS]: 'var(--pink-soft)',
};

const INSIGHT_BULLET_COLOR: Record<InsightVariant, string> = {
  [CATEGORY_INSIGHT_VARIANT.STRENGTHS]: 'var(--positive-green)',
  [CATEGORY_INSIGHT_VARIANT.DIFFERENCES]: 'var(--blue-vivid)',
  [CATEGORY_INSIGHT_VARIANT.WEAKNESSES]: 'var(--negative-orange)',
  [CATEGORY_INSIGHT_VARIANT.ACTIONS]: 'var(--flamingo-pink)',
};

type Props = {
  title: string;
  items: string[];
  variant: InsightVariant;
  categoryLabel?: string;
};

const Section = styled.div``;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
`;

const SectionIcon = styled.div<{ variant: InsightVariant }>`
  width: 20px;
  height: 20px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${({ variant }) => SECTION_ICON_BACKGROUND[variant]};

  img {
    width: 14px;
    height: 14px;
  }
`;

const SectionTitle = styled.div`
  font-size: 16px;
  font-family: var(--font-bold);
  color: var(--text-primary);
`;

const InsightList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
`;

const InsightItem = styled.li<{ variant: InsightVariant }>`
  display: flex;
  gap: 12px;
  font-size: 14px;
  color: var(--text-secondary);
  line-height: 1.45;

  & + & {
    margin-top: 12px;
  }

  &::before {
    content: '';
    width: 4px;
    height: 4px;
    min-width: 4px;
    border-radius: 50%;
    position: relative;
    top: 9px;
    background: ${({ variant }) => INSIGHT_BULLET_COLOR[variant]};
  }
`;

const getSectionTitle = (title: string, categoryLabel?: string) =>
  categoryLabel ? `${title} (${categoryLabel})` : title;

const CategoryInsightSection = ({ title, items, variant, categoryLabel }: Props) => {
  if (items.length === 0) {
    return null;
  }

  return (
    <Section>
      <SectionHeader>
        <SectionIcon variant={variant}>
          <img src={SECTION_ICON_SRC[variant]} alt="" />
        </SectionIcon>
        <SectionTitle>{getSectionTitle(title, categoryLabel)}</SectionTitle>
      </SectionHeader>
      <InsightList>
        {items.map((item, index) => (
          <InsightItem key={`${variant}-${index}`} variant={variant}>
            {item}
          </InsightItem>
        ))}
      </InsightList>
    </Section>
  );
};

export default CategoryInsightSection;
